import React, { useState, useEffect, useMemo } from 'react';
import { Users, Calendar as CalendarIcon, ArrowRightLeft, ShoppingBag } from 'lucide-react';
import { adminStaffService } from '../../services/adminStaffService';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend } from 'recharts';

// Đảm bảo import đúng đường dẫn file constants của bạn
import { 
    PRODUCT_STATUS, PRODUCT_STATUS_CONFIG, 
    ORDER_STATUS, ORDER_STATUS_CONFIG 
} from '../../utils/constants';

const getVietnamDateString = (dateObj) => {
    // Ép về múi giờ Việt Nam và lấy định dạng YYYY-MM-DD
    const parts = dateObj.toLocaleString("en-CA", { 
        timeZone: "Asia/Ho_Chi_Minh", 
        year: 'numeric', 
        month: '2-digit', 
        day: '2-digit' 
    });
    return parts; // Trả về dạng "YYYY-MM-DD"
};

const Dashboard = () => {
    const [loading, setLoading] = useState(true);
    
    const [allOrders, setAllOrders] = useState([]);
    const [allTraffic, setAllTraffic] = useState([]); 
    
    const [stats, setStats] = useState({ 
        orders: {}, 
        products: {}, 
        totalUsers: 0 
    });
    
    const [timeRange, setTimeRange] = useState('week'); 
    const [compareWithLastYear, setCompareWithLastYear] = useState(false); 

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                const [ordersRes, itemsRes, usersRes, trafficRes] = await Promise.all([
                    adminStaffService.getAllOrders().catch(() => []), 
                    adminStaffService.getAllItems().catch(() => []),
                    adminStaffService.getAllAccounts().catch(() => []),
                    adminStaffService.getTraffic().catch(() => []) 
                ]);
                
                const ordersData = Array.isArray(ordersRes?.data) ? ordersRes.data : (Array.isArray(ordersRes) ? ordersRes : []);
                setAllOrders(ordersData);
                
                setAllTraffic(Array.isArray(trafficRes?.data) ? trafficRes.data : (Array.isArray(trafficRes) ? trafficRes : []));

                // 1. Phân loại và đếm Đơn hàng theo trạng thái
                const orderCounts = {};
                Object.values(ORDER_STATUS).forEach(status => orderCounts[status] = 0);
                ordersData.forEach(order => {
                    const status = order.status !== undefined ? order.status : order.orderStatus;
                    if (orderCounts[status] !== undefined) {
                        orderCounts[status]++;
                    }
                });

                // 2. Phân loại và đếm Sản phẩm theo trạng thái
                const itemsData = Array.isArray(itemsRes?.data) ? itemsRes.data : (Array.isArray(itemsRes) ? itemsRes : []);
                const productCounts = {};
                Object.values(PRODUCT_STATUS).forEach(status => productCounts[status] = 0);
                itemsData.forEach(item => {
                    const status = item.status !== undefined ? item.status : item.itemStatus;
                    if (productCounts[status] !== undefined) {
                        productCounts[status]++;
                    }
                });
                
                // 3. Đếm tài khoản khách hàng
                const allAccounts = Array.isArray(usersRes?.data) ? usersRes.data : (Array.isArray(usersRes) ? usersRes : []);
                const guestAccounts = allAccounts.filter(acc => {
                    const roleName = acc.role?.roleName || acc.role || '';
                    return roleName === 'ROLE_CUSTOMER'; 
                });
                
                setStats({ 
                    orders: orderCounts,
                    products: productCounts,
                    totalUsers: guestAccounts.length 
                });

            } catch (error) { 
                console.error("Lỗi khi tải dữ liệu Dashboard:", error); 
            } finally { 
                setLoading(false); 
            }
        };
        fetchDashboardData();
    }, []);

    const chartData = useMemo(() => {
        const data = [];
        const now = new Date();
        const currentYear = now.getFullYear();

        const getTrafficCount = (dateStr) => {
            const record = allTraffic.find(t => t.visitDate && t.visitDate.startsWith(dateStr));
            return record ? record.visitCount : 0;
        };

        // Hàm tiện ích: Lấy Số lượng Đơn hàng theo Trạng thái
        const getOrdersCountByStatus = (datePrefix, statusArray) => {
            return allOrders.filter(o => {
                if (!o.orderDate) return false;
                
                // Chuyển orderDate sang múi giờ VN để so sánh với datePrefix
                const orderDateInVN = getVietnamDateString(new Date(o.orderDate));
                
                const st = o.status !== undefined ? o.status : o.orderStatus;
                return statusArray.includes(Number(st)) && orderDateInVN === datePrefix;
            }).length;
        };

        if (timeRange === 'week' || timeRange === 'month') {
            const days = timeRange === 'week' ? 6 : 29;
            for (let i = days; i >= 0; i--) {
                const d = new Date(now);
                d.setDate(d.getDate() - i);
                const dateStr = getVietnamDateString(d); 
                
                // Chỉ đếm đơn hàng đã giao thành công
                const orders = getOrdersCountByStatus(dateStr, [ORDER_STATUS.DELIVERED]);
                
                data.push({ name: `${d.getDate()}/${d.getMonth() + 1}`, orders, traffic: getTrafficCount(dateStr) });
            }
        } else if (timeRange === 'year') {
            for (let i = 1; i <= 12; i++) {
                const monthStr = i.toString().padStart(2, '0');
                const prefixThisYear = `${currentYear}-${monthStr}`;
                const prefixLastYear = `${currentYear - 1}-${monthStr}`;
                
                const ordersThisYear = getOrdersCountByStatus(prefixThisYear, [ORDER_STATUS.DELIVERED]);
                const trafficThisYear = allTraffic.filter(t => t.visitDate && t.visitDate.startsWith(prefixThisYear))
                                                  .reduce((sum, record) => sum + record.visitCount, 0);

                let pointData = { 
                    name: `T${i}`, 
                    orders: ordersThisYear, 
                    traffic: trafficThisYear 
                };

                if (compareWithLastYear) {
                    pointData.lastYearOrders = getOrdersCountByStatus(prefixLastYear, [ORDER_STATUS.DELIVERED]);
                    pointData.lastYearTraffic = allTraffic.filter(t => t.visitDate && t.visitDate.startsWith(prefixLastYear))
                                                          .reduce((sum, record) => sum + record.visitCount, 0);
                }

                data.push(pointData);
            }
        }
        return data;
    }, [allOrders, allTraffic, timeRange, compareWithLastYear]);

    // COMPONENT THẺ THỐNG KÊ
    const StatCard = ({ title, value, config }) => {
        const { Icon, color, bg, border } = config;
        return (
            <div className={`bg-white rounded-xl shadow-sm border ${border || 'border-gray-100'} p-3.5 flex items-center gap-3 hover:shadow-md transition-all group`}>
                <div className={`${bg} ${color} p-2.5 rounded-lg transition-transform group-hover:scale-110`}>
                    <Icon size={20} />
                </div>
                <div>
                    <p className="text-xs font-medium text-gray-500 mb-0.5 line-clamp-1" title={title}>{title}</p>
                    <h3 className="text-xl font-bold text-gray-800">{loading ? '...' : value}</h3>
                </div>
            </div>
        );
    };

    return (
        <div className="p-4 sm:p-6 lg:p-8 bg-gray-50 min-h-screen">
            <div className="flex justify-between items-center mb-5 border-b border-gray-200 pb-2">
                <h2 className="text-2xl font-bold text-[#002855] inline-block border-b-tnc-orange">Tổng quan Hệ thống</h2>
            </div>
            
            {/* --- PHÂN KHU 1: THỐNG KÊ ĐƠN HÀNG --- */}
            <div className="mb-5">
                <h3 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                    <ShoppingBag className="text-tnc-orange" size={18}/> Tình trạng Đơn hàng
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-4 gap-3">
                    {Object.values(ORDER_STATUS).map((statusKey) => (
                        <StatCard 
                            key={`order-${statusKey}`}
                            title={ORDER_STATUS_CONFIG[statusKey].text} 
                            value={stats.orders[statusKey] || 0} 
                            config={ORDER_STATUS_CONFIG[statusKey]} 
                        />
                    ))}
                </div>
            </div>

            {/* --- PHÂN KHU 2: THỐNG KÊ SẢN PHẨM & TÀI KHOẢN --- */}
            <div className="grid grid-cols-1 xl:grid-cols-4 gap-5 mb-6">
                <div className="xl:col-span-3">
                    <h3 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                        <ShoppingBag className="text-tnc-orange" size={18}/> Tình trạng Sản phẩm
                    </h3>
                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                        {Object.values(PRODUCT_STATUS).map((statusKey) => (
                            <StatCard 
                                key={`product-${statusKey}`}
                                title={PRODUCT_STATUS_CONFIG[statusKey].text} 
                                value={stats.products[statusKey] || 0} 
                                config={PRODUCT_STATUS_CONFIG[statusKey]} 
                            />
                        ))}
                    </div>
                </div>

                <div className="xl:col-span-1">
                    <h3 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                        <Users className="text-tnc-orange" size={18}/> Khách hàng
                    </h3>
                    <div className="bg-white rounded-xl shadow-sm border border-green-200 p-4 flex items-center gap-3 hover:shadow-md transition-all group h-[calc(100%-28px)]">
                        <div className="bg-green-100 text-green-600 p-3 rounded-lg transition-transform group-hover:scale-110">
                            <Users size={24} />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-500 mb-1">Tài khoản khách</p>
                            <h3 className="text-2xl font-bold text-gray-800">{loading ? '...' : stats.totalUsers}</h3>
                        </div>
                    </div>
                </div>
            </div>

            {/* --- PHÂN KHU 3: BIỂU ĐỒ --- */}
            <div className="flex flex-col sm:flex-row justify-between items-center bg-white p-4 rounded-xl shadow-sm border border-gray-100 mb-5 gap-4">
                <h3 className="font-bold text-gray-800 flex items-center gap-2">
                    <CalendarIcon className="text-tnc-orange" size={20}/> Biểu đồ Phân tích
                </h3>
                <div className="flex items-center gap-4">
                    {timeRange === 'year' && (
                        <label className="flex items-center gap-2 cursor-pointer bg-gray-50 px-3 py-2 rounded-lg border border-gray-200 hover:border-tnc-orange transition-colors">
                            <input 
                                type="checkbox" 
                                checked={compareWithLastYear} 
                                onChange={(e) => setCompareWithLastYear(e.target.checked)}
                                className="w-4 h-4 accent-tnc-orange"
                            />
                            <span className="text-sm font-bold text-gray-700 flex items-center gap-1"><ArrowRightLeft size={14}/> So sánh năm trước</span>
                        </label>
                    )}
                    <select 
                        value={timeRange} 
                        onChange={(e) => setTimeRange(e.target.value)}
                        className="border-2 border-gray-200 rounded-lg px-4 py-2 font-bold text-gray-700 outline-none focus:border-tnc-orange cursor-pointer bg-gray-50 hover:bg-gray-100 text-sm"
                    >
                        <option value="week">7 Ngày qua</option>
                        <option value="month">30 Ngày qua</option>
                        <option value="year">Tổng kết Năm</option>
                    </select>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 flex flex-col">
                    <h3 className="font-bold text-gray-800 flex items-center gap-2 mb-4">
                        <ShoppingBag className="text-tnc-orange" size={20}/> Đơn hàng Giao thành công
                    </h3>
                    <div className="h-72 w-full mt-auto">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fontSize: 11, fill: '#6b7280'}} dy={10} minTickGap={15} />
                                <YAxis allowDecimals={false} axisLine={false} tickLine={false} tick={{fontSize: 12, fill: '#6b7280'}} />
                                <Tooltip cursor={{fill: '#f3f4f6'}} contentStyle={{borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'}} />
                                <Legend verticalAlign="top" height={36} wrapperStyle={{ fontSize: '12px' }}/>
                                
                                {compareWithLastYear && timeRange === 'year' && (
                                    <Bar dataKey="lastYearOrders" name={`Năm ${new Date().getFullYear() - 1}`} fill="#e5e7eb" radius={[4, 4, 0, 0]} maxBarSize={30} />
                                )}
                                
                                <Bar dataKey="orders" name={timeRange === 'year' ? `Năm ${new Date().getFullYear()}` : 'Đơn thành công'} fill="#FF5500" radius={[4, 4, 0, 0]} maxBarSize={40} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 flex flex-col">
                    <h3 className="font-bold text-gray-800 flex items-center gap-2 mb-4">
                        <Users className="text-tnc-orange" size={20}/> Lượng truy cập (Traffic)
                    </h3>
                    <div className="h-72 w-full mt-auto">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="colorTraffic" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#FF5500" stopOpacity={0.3}/>
                                        <stop offset="95%" stopColor="#FF5500" stopOpacity={0}/>
                                    </linearGradient>
                                    <linearGradient id="colorTrafficOld" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#9ca3af" stopOpacity={0.3}/>
                                        <stop offset="95%" stopColor="#9ca3af" stopOpacity={0}/>
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fontSize: 11, fill: '#6b7280'}} dy={10} minTickGap={15} />
                                <YAxis axisLine={false} tickLine={false} tick={{fontSize: 12, fill: '#6b7280'}} />
                                <Tooltip contentStyle={{borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'}} />
                                {compareWithLastYear && timeRange === 'year' && <Legend verticalAlign="top" height={36}/>}
                                {compareWithLastYear && timeRange === 'year' && (
                                    <Area type="monotone" dataKey="lastYearTraffic" name={`Năm ${new Date().getFullYear() - 1}`} stroke="#9ca3af" strokeWidth={2} fill="url(#colorTrafficOld)" />
                                )}
                                <Area type="monotone" dataKey="traffic" name={timeRange === 'year' ? `Năm ${new Date().getFullYear()}` : 'Lượt truy cập'} stroke="#FF5500" strokeWidth={3} fillOpacity={1} fill="url(#colorTraffic)" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;